import type { GeoMapStyle } from "~/api/types";

type RasterSource = {
  tiles: string[];
  attribution: string;
  maxzoom: number;
};

/**
 * Raster tile sources rather than a vector provider, so there is no API key or
 * billing account to configure. OpenStreetMap's tile policy expects modest
 * volume and visible attribution; swap in a paid provider before any serious
 * traffic.
 */
const SOURCES: Record<GeoMapStyle, RasterSource> = {
  streets: {
    tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
    attribution: "© OpenStreetMap contributors",
    maxzoom: 19,
  },
  satellite: {
    tiles: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    ],
    attribution: "Esri, Maxar, Earthstar Geographics",
    maxzoom: 19,
  },
  outdoors: {
    tiles: ["https://tile.opentopomap.org/{z}/{x}/{y}.png"],
    attribution: "© OpenStreetMap contributors, SRTM | © OpenTopoMap",
    maxzoom: 17,
  },
  terrain: {
    tiles: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    ],
    attribution: "Esri, HERE, Garmin, USGS",
    maxzoom: 19,
  },
};

export const GEO_MAP_STYLE_OPTIONS: { value: GeoMapStyle; label: string }[] = [
  { value: "streets", label: "Streets" },
  { value: "satellite", label: "Satellite" },
  { value: "outdoors", label: "Topographic" },
  { value: "terrain", label: "Terrain" },
];

export function mapStyleFor(style: GeoMapStyle) {
  const source = SOURCES[style] ?? SOURCES.streets;
  return {
    version: 8 as const,
    sources: {
      base: {
        type: "raster" as const,
        tiles: source.tiles,
        tileSize: 256,
        maxzoom: source.maxzoom,
        attribution: source.attribution,
      },
    },
    layers: [
      {
        id: "base",
        type: "raster" as const,
        source: "base",
      },
    ],
  };
}

/** Auckland, used only until a collection has its own centre. */
export const DEFAULT_MAP_CENTER = { lat: -36.8485, lng: 174.7633 };
export const DEFAULT_MAP_ZOOM = 17;
